// src/features/MediaLibrary/MediaService.js
import { 
    collection, 
    doc, 
    addDoc, 
    getDoc, 
    getDocs, 
    updateDoc, 
    deleteDoc,
    query, 
    where,
    arrayUnion,
    arrayRemove,
    serverTimestamp 
  } from 'firebase/firestore';
  import { 
    ref, 
    uploadBytes, 
    getDownloadURL, 
    deleteObject 
  } from 'firebase/storage';
  import { db, storage } from '../../firebase/config';
  
  class MediaService {
    constructor() {
      this.collectionName = 'imageAssets';
    }
  
    // Upload media file to Firebase Storage
 async uploadMediaFile(file, topic, metadata = {}) {
  try {
    console.log('🔍 Upload Debug Info:');
    console.log('File:', file.name, file.type, file.size);
    console.log('Topic:', topic);
    
    // Check auth state
    const { auth } = await import('../../firebase/config');
    console.log('User authenticated:', auth.currentUser ? 'Yes' : 'No');
    console.log('User UID:', auth.currentUser?.uid);
    
    // Sanitize filename (fix for spaces)
    const sanitizedName = file.name
      .replace(/\s+/g, '_')           // Replace spaces with underscores
      .replace(/[^a-zA-Z0-9._-]/g, '') // Remove special characters
      .toLowerCase();
    
    const filename = `${Date.now()}_${sanitizedName}`;
    console.log('Original filename:', file.name);
    console.log('Sanitized filename:', filename);
    
    const folderPath = file.type.startsWith('image') ? 'images' : 'audio';
    const path = `media/${folderPath}/topics/${topic}/${filename}`;
    console.log('Storage path:', path);
    
    const storageRef = ref(storage, path);
    
    // Upload file with metadata
    const uploadMetadata = {
      contentType: file.type,
      customMetadata: {
        topic: topic,
        originalName: file.name,
        ...metadata
      }
    };
    
    console.log('Upload metadata:', uploadMetadata);
    console.log('🚀 Starting upload...');
    
    const snapshot = await uploadBytes(storageRef, file, uploadMetadata);
    console.log('✅ Upload successful');
    
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('✅ Download URL obtained');
    
    return { downloadURL, storagePath: path };
  } catch (error) {
    console.error('❌ Detailed upload error:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
}
  
    // Create media asset document in Firestore
    async createMediaAsset(assetData) {
      try {
        const mediaAssetRef = collection(db, this.collectionName);
        const newAsset = {
          ...assetData,
          uploadedAt: serverTimestamp(),
          usedInUnits: [],
          usedInLessons: []
        };
        
        const docRef = await addDoc(mediaAssetRef, newAsset);
        return { id: docRef.id, ...newAsset };
      } catch (error) {
        console.error('Error creating media asset:', error);
        throw error;
      }
    }
  
    // Get all media assets
    async getAllMediaAssets() {
      try {
        const mediaSnapshot = await getDocs(collection(db, this.collectionName));
        return mediaSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error fetching media assets:', error);
        throw error;
      }
    }
  
    // Get media assets by topic
    async getMediaByTopic(topic) {
      try {
        const q = query(
          collection(db, this.collectionName),
          where('topics', 'array-contains', topic)
        );
        const mediaSnapshot = await getDocs(q);
        return mediaSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error fetching media by topic:', error);
        throw error;
      }
    }
  
    // Update media asset usage
    async addUnitUsage(mediaId, unitId) {
      try {
        const mediaRef = doc(db, this.collectionName, mediaId);
        await updateDoc(mediaRef, {
          usedInUnits: arrayUnion(unitId)
        });
      } catch (error) {
        console.error('Error updating media usage:', error);
        throw error;
      }
    }
  
    // Remove unit usage
    async removeUnitUsage(mediaId, unitId) {
      try {
        const mediaRef = doc(db, this.collectionName, mediaId);
        await updateDoc(mediaRef, {
          usedInUnits: arrayRemove(unitId)
        });
      } catch (error) {
        console.error('Error removing media usage:', error);
        throw error;
      }
    }
  
    // Delete media asset
    async deleteMediaAsset(mediaId) {
      try {
        // Get the asset first to check if it's in use
        const mediaRef = doc(db, this.collectionName, mediaId);
        const mediaDoc = await getDoc(mediaRef);
        
        if (!mediaDoc.exists()) {
          throw new Error('Media asset not found');
        }
        
        const mediaData = mediaDoc.data();
        
        // Check if the asset is in use
        if (mediaData.usedInUnits && mediaData.usedInUnits.length > 0) {
          throw new Error('Cannot delete media asset that is in use');
        }
        
        // Delete from Storage
        if (mediaData.storagePath) {
          const storageRef = ref(storage, mediaData.storagePath);
          await deleteObject(storageRef);
        }
        
        // Delete from Firestore
        await deleteDoc(mediaRef);
        
        return { success: true };
      } catch (error) {
        console.error('Error deleting media asset:', error);
        throw error;
      }
    }
  
    // Get media by category
    async getMediaByCategory(category) {
      try {
        const q = query(
          collection(db, this.collectionName),
          where('category', '==', category)
        );
        const mediaSnapshot = await getDocs(q);
        return mediaSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error fetching media by category:', error);
        throw error;
      }
    }
  
    // Search media by tags
    async searchMediaByTags(tags) {
      try {
        const q = query(
          collection(db, this.collectionName),
          where('tags', 'array-contains-any', tags)
        );
        const mediaSnapshot = await getDocs(q);
        return mediaSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error searching media by tags:', error);
        throw error;
      }
    }
  }
  
  export default new MediaService();